import {useDrawer} from "@/contexts/DrawerContext";
import {Button} from "@/components/ui/Button";
import {X} from "lucide-react";
import {DRAWER_IDS} from "@/components/Drawers";
import {useCallback, useMemo, useState, useEffect} from "react";

import {Updater, RowSelectionState} from "@tanstack/react-table";
import {PlayCircle, SearchIcon} from "lucide-react";
import {Node} from "@xyflow/react";
import {FlowNodeData} from "@/types/node";

import useCustomActionResultsColumns from "@/hooks/useCustomActionResultsColumns";

import {
  CustomActionRecordModel,
  CustomActionResultRecordModel,
} from "@/api/models";
import {useInitializeCustomAction} from "@/api/services/queries";
import CustomActionModal, {
  Variable,
} from "@/components/shared/CustomActionModal";
import {Checkbox} from "@/components/ui/Checkbox";
import DataTable from "@/components/ui/DataTable";
import {Input} from "@/components/ui/input";
import JsonContainer from "@/components/ui/JsonContainer";
import {Label} from "@/components/ui/label";
import {
  extractUrlVariables,
  extractBodyVariables,
} from "@/utils/customAction/customVariables";
import {parseJsonStrings} from "@/utils/parseJsonStrings";
import {useSelectedElements} from "@/hooks/useSelectedElements";
import {getSettingValueByKey} from "@/utils/index";
import useCustomActionResultsManage from "@/hooks/useCustomActionResultsManage";
import {flattenKeys} from "@/utils/flattenKeys";
import {useNodeUpdater} from "@/hooks/useNodeUpdater";
import {useSave} from "@/contexts/SaveContext";

const InitializeAction = ({
  selectedNode,
}: {
  selectedNode: Node<FlowNodeData>;
}) => {
  const {Drawer, isDrawerOpen, closeDrawer} = useDrawer();
  const DRAWER_ID = DRAWER_IDS.INITIALIZE_ACTION;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const actionData = useMemo(() => {
    const data = getSettingValueByKey(
      selectedNode?.data?.settings,
      "action",
    ) as any;
    return data?.actionRecord || data;
  }, [selectedNode?.data?.settings]);

  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [onlySelected, setOnlySelected] = useState(false);

  const [apiResponse, setApiResponse] = useState<{[key: string]: unknown}>(
    actionData?.api_response || {},
  );

  const {updateNodeSettingParam} = useNodeUpdater();
  const {handleSaveAction, handleSave} = useSave();

  const actionId = actionData?.action_id;
  const variablesFromForm = useMemo(
    () => actionData?.variables || [],
    [actionData?.variables],
  );
  const queryParametersForm = useMemo(
    () => actionData?.queryParameters || [],
    [actionData?.queryParameters],
  );

  useEffect(() => {
    setApiResponse(actionData?.api_response || {});
  }, [actionData?.api_response]);

  const queryVariables = useMemo(() => {
    const queryParams = queryParametersForm
      ?.filter((field: any) => field.key?.trim() !== "")
      ?.map((field: any) => field.key);
    if (!queryParams?.length) return [];
    return queryParams
      .map(param => {
        if (actionData?.query_params?.[param]) {
          return {
            key: param,
            value: actionData.query_params[param],
          };
        }
      })
      .filter(Boolean);
  }, [actionData?.query_params, queryParametersForm]);

  useEffect(() => {
    if (variablesFromForm) {
      const initialSelection = variablesFromForm.reduce(
        (acc, key) => {
          acc[key] = true;
          return acc;
        },
        {} as Record<string, boolean>,
      );
      setSelectedRows(initialSelection);
    }
  }, [variablesFromForm]);

  const flattenedRecords = useMemo<CustomActionResultRecordModel[]>(() => {
    if (!apiResponse) return [];
    return flattenKeys(
      apiResponse as unknown,
    ) as CustomActionResultRecordModel[];
  }, [apiResponse]);

  const {search, setSearch, pagination, setPagination, resultsData} =
    useCustomActionResultsManage({
      data: flattenedRecords,
      selectedRows,
      setSelectedRows,
      onlySelected,
      setOnlySelected,
    });

  const {columns} = useCustomActionResultsColumns();

  const variablesBeforeTheCall = useMemo(
    () => actionData?.variablesBeforeTheCall || [],
    [actionData?.variablesBeforeTheCall],
  );
  const llmVars = useMemo(
    () => actionData?.llm_vars || [],
    [actionData?.llm_vars],
  );
  const apiUrl = actionData?.apiUrl;
  const body = actionData?.body ? JSON.stringify(actionData.body) : "";
  const apiHeaders = useMemo(
    () => actionData?.apiHeaders || [],
    [actionData?.apiHeaders],
  );

  const variables = useMemo<Variable[]>(() => {
    const allVariables = [
      // Before call variables - convert from KeyValuePair to Variable format
      ...(variablesBeforeTheCall?.map((v: any) => ({
        name: v.key || "",
        value: v.value || "",
      })) || []),

      // LLM variables - convert from llm_vars format
      ...(llmVars?.map((v: any) => ({
        name: v.key || v.name || "",
        value: v.example || v.value || "",
      })) || []),

      // Query parameters - convert from KeyValuePair to Variable format
      ...(queryVariables?.map((v: any) => ({
        name: v.key || "",
        value: v.value || "",
      })) || []),

      // URL variables
      ...(apiUrl
        ? extractUrlVariables(apiUrl).map(name => ({name, value: ""}))
        : []),

      // Body variables
      ...(body
        ? extractBodyVariables(body).map(name => ({name, value: name}))
        : []),

      // Headers - convert from KeyValuePair to Variable format
      ...(apiHeaders
        ?.filter((h: any) => h.key?.trim())
        .map((h: any) => ({
          name: h.key || "",
          value: h.value || "",
        })) || []),
    ];

    // Remove duplicates by name
    const seen = new Set<string>();
    return allVariables.filter(item => {
      if (seen.has(item.name)) return false;
      seen.add(item.name);
      return true;
    });
  }, [
    variablesBeforeTheCall,
    llmVars,
    queryVariables,
    apiUrl,
    body,
    apiHeaders,
  ]);

  const {mutateAsync: initializeCustomAction, isPending} =
    useInitializeCustomAction();

  const handleInitialize = useCallback(
    async (values: Record<string, string>) => {
      if (!actionId) return;
      const example_variables = Object.entries(values).map(([key, value]) => ({
        key,
        value,
      }));
      const response = await initializeCustomAction({
        actionId,
        data: {
          example_variables,
        },
      });
      const parsed = parseJsonStrings(response);
      setApiResponse(parsed);
      setIsModalOpen(false);
    },
    [actionId, initializeCustomAction],
  );

  const handleInitializeClick = useCallback(() => {
    if (variables.length === 0) {
      handleInitialize({});
    } else {
      setIsModalOpen(true);
    }
  }, [handleInitialize, variables]);

  const handleRowSelectionChange = (updater: Updater<RowSelectionState>) => {
    const newSelectedRows =
      typeof updater === "function" ? updater(selectedRows) : updater;
    const newSelectedVariables = Object.keys(newSelectedRows).filter(
      key => newSelectedRows[key],
    ) as string[];

    const updatedAction = {
      ...actionData,
      variables: newSelectedVariables,
      api_response: apiResponse,
    };

    // Get full action data structure from settings
    const fullActionData = getSettingValueByKey(
      selectedNode?.data?.settings,
      "action",
    ) as any;

    updateNodeSettingParam(selectedNode?.id, "action", {
      ...fullActionData,
      variables: newSelectedVariables,
      actionRecord: updatedAction,
    });

    handleSaveAction(actionId, {
      variables: newSelectedVariables,
      api_response: apiResponse,
    });
    handleSave();
    setSelectedRows(newSelectedRows);
  };

  return (
    isDrawerOpen(DRAWER_ID) && (
      <Drawer
        key={actionId}
        id={DRAWER_ID}
        position="right"
        width="w-110"
        disableClickOutside={true}
        className="bg-[#F8F9FA] border-gray-200 rounded-r-0 pr-[900px]"
      >
        <div className="flex flex-col h-full min-w-[460px]">
          <div className="flex justify-between items-center gap-1 px-5 py-5 pb-4">
            <h2 className="text-base font-semibold text-ellipsis overflow-hidden whitespace-nowrap">
              Response
            </h2>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => closeDrawer(DRAWER_ID)}
              className="[&_svg]:size-5"
            >
              <X />
            </Button>
          </div>
          <div className="space-y-4 max-h-full overflow-y-auto px-5 pb-5">
            <div>
              <div className="flex gap-16">
                <div className="flex-1">
                  <Button
                    variant="outline"
                    className="mb-5"
                    onClick={handleInitializeClick}
                    disabled={isPending}
                  >
                    <PlayCircle className="w-4.5 h-4.5 text-primary-accent" />{" "}
                    Initialize action
                  </Button>
                  <JsonContainer data={apiResponse} collapsed={1} />
                </div>
              </div>
              <div className="flex gap-16">
                <div className="flex-1 w-full">
                  <div className="flex justify-between items-center gap-1 pt-5 pb-4">
                    <h2 className="text-base font-semibold text-ellipsis overflow-hidden whitespace-nowrap">
                      Action Results
                    </h2>
                  </div>
                  <div className="flex items-center gap-2 mb-4">
                    <Input
                      startContent={
                        <SearchIcon className="w-4 h-4 text-disabled" />
                      }
                      placeholder="Search by name"
                      containerClassName="flex-1"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={onlySelected}
                        onCheckedChange={checked =>
                          setOnlySelected(checked as boolean)
                        }
                        id="only-selected"
                      />
                      <Label htmlFor="only-selected">Only show selected</Label>
                    </div>
                  </div>
                  <div data-drawer-content="INITIALIZE_ACTION">
                    <DataTable
                      columns={columns}
                      data={resultsData?.records || []}
                      pagination={pagination}
                      onPaginationChange={setPagination}
                      onRowSelectionChange={handleRowSelectionChange}
                      rowSelection={selectedRows}
                      getRowId={(row: CustomActionResultRecordModel) => row.id}
                      totalCount={resultsData?.pagination?.totalCount}
                      hideRowsPerPage={true}
                    />
                  </div>
                </div>
              </div>
            </div>
            <CustomActionModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onInitialize={handleInitialize}
              variables={variables}
            />
          </div>
        </div>
      </Drawer>
    )
  );
};

export default InitializeAction;
